import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../lib/AuthContext';
import { useOutlet } from '../lib/OutletContext';
import {
  getPurchaseReconciliationQueue,
  resolvePurchaseReconciliation,
} from '../lib/api';
import { useReconciliationBadge } from '../lib/ReconciliationBadgeContext';
import { AlertTriangle, CheckCircle2, Split, RefreshCw, X } from 'lucide-react';
import { formatNumber } from '../lib/utils';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import logger from '../lib/logger';

const STATUS_TABS = [
  { key: 'PENDING', label: 'Pending' },
  { key: 'RESOLVED', label: 'Resolved' },
];

const DECISION_LABEL = {
  INCLUDED_IN_AUDIT: 'Included in audit',
  NOT_INCLUDED: 'Not included',
  SPLIT: 'Split',
};

const PurchaseReconciliation = () => {
  const { user } = useAuth();
  const { selectedOutlet } = useOutlet();
  const { refresh: refreshBadge } = useReconciliationBadge();
  const [statusTab, setStatusTab] = useState('PENDING');
  const [items, setItems] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [resolvedCount, setResolvedCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [splitDialog, setSplitDialog] = useState(null); // { row, uncounted, notes }
  const [resolving, setResolving] = useState(false);

  const canResolve =
    !!user?.is_super_admin ||
    !!user?.is_org_admin ||
    (user?.roles || []).some((r) => {
      const u = String(r).toUpperCase();
      return (
        u === 'PROCUREMENT_MANAGER' ||
        u === 'ORG_ADMIN' ||
        u === 'OUTLET_ADMIN' ||
        (u.includes('PROCUREMENT') && (u.includes('MANAGER') || u.includes('MGR')))
      );
    });

  const load = useCallback(async () => {
    if (!selectedOutlet) return;
    setLoading(true);
    try {
      const data = await getPurchaseReconciliationQueue(selectedOutlet, statusTab);
      setItems(data.items || []);
      setPendingCount(data.pending_count || 0);
      setResolvedCount(data.resolved_count || 0);
      // Keep the global nav badge in sync with what the page sees
      refreshBadge();
    } catch (e) {
      logger.error(e);
      toast.error('Failed to load reconciliation queue');
    } finally {
      setLoading(false);
    }
  }, [selectedOutlet, statusTab, refreshBadge]);

  useEffect(() => {
    load();
  }, [load]);

  const handleQuickDecision = async (row, decision) => {
    if (!canResolve) {
      toast.error('Only org admins or procurement managers can resolve.');
      return;
    }
    setResolving(true);
    try {
      await resolvePurchaseReconciliation(row.queue_id, decision);
      toast.success(`Resolved as ${DECISION_LABEL[decision]}`);
      await load();
    } catch (e) {
      logger.error(e);
      toast.error(e?.response?.data?.detail || 'Failed to resolve');
    } finally {
      setResolving(false);
    }
  };

  const handleSplitSubmit = async () => {
    if (!splitDialog) return;
    const { row, uncounted, notes } = splitDialog;
    const uq = parseFloat(uncounted);
    if (Number.isNaN(uq) || uq < 0 || uq > row.quantity) {
      toast.error(`Uncounted quantity must be between 0 and ${row.quantity}`);
      return;
    }
    setResolving(true);
    try {
      await resolvePurchaseReconciliation(row.queue_id, 'SPLIT', uq, notes || null);
      toast.success(`Split applied: ${uq} ${row.base_unit} added to stock`);
      setSplitDialog(null);
      await load();
    } catch (e) {
      logger.error(e);
      toast.error(e?.response?.data?.detail || 'Failed to resolve');
    } finally {
      setResolving(false);
    }
  };

  return (
    <div className="container mx-auto px-4 md:px-8 py-6" data-testid="purchase-reconciliation-page">
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-black tracking-tight flex items-center gap-3">
            <AlertTriangle className="w-7 h-7 text-amber-400" />
            Late Pre-Audit Reconciliation
          </h1>
          <p className="text-sm text-zinc-400 mt-1 max-w-2xl">
            Bills dated <span className="text-white">on or before</span> the latest opening
            stock or audit anchor are parked here. Operator must decide whether the goods
            were already counted, or are genuinely missing from current stock.
          </p>
        </div>
        <button
          onClick={load}
          className="px-3 py-2 rounded-md bg-[#27272a] hover:bg-[#3f3f46] text-sm flex items-center gap-2"
          data-testid="reconciliation-refresh-button"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-[#27272a] mb-4">
        {STATUS_TABS.map((tab) => {
          const count = tab.key === 'PENDING' ? pendingCount : resolvedCount;
          const active = statusTab === tab.key;
          return (
            <button
              key={tab.key}
              onClick={() => setStatusTab(tab.key)}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                active
                  ? 'border-emerald-500 text-emerald-400'
                  : 'border-transparent text-zinc-400 hover:text-white'
              }`}
              data-testid={`reconciliation-tab-${tab.key.toLowerCase()}`}
            >
              {tab.label}
              <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-[#27272a]">
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {/* Content */}
      {loading ? (
        <div className="text-zinc-400 py-12 text-center" data-testid="reconciliation-loading">
          Loading…
        </div>
      ) : items.length === 0 ? (
        <div
          className="rounded-lg border border-[#27272a] bg-[#0f0f12] p-12 text-center"
          data-testid="reconciliation-empty"
        >
          <CheckCircle2 className="w-10 h-10 text-emerald-500 mx-auto mb-3" />
          <p className="text-zinc-300 font-medium">
            No {statusTab.toLowerCase()} reconciliation items.
          </p>
          <p className="text-zinc-500 text-sm mt-1">
            Late bills (dated on or before the latest audit/opening anchor) will appear here.
          </p>
        </div>
      ) : (
        <div className="rounded-lg border border-[#27272a] overflow-hidden">
          <table className="w-full text-sm" data-testid="reconciliation-table">
            <thead className="bg-[#18181b] text-zinc-400 text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-3">Ingredient</th>
                <th className="text-left px-4 py-3">Bill Date</th>
                <th className="text-left px-4 py-3">Anchor</th>
                <th className="text-right px-4 py-3">Quantity</th>
                <th className="text-right px-4 py-3">Value</th>
                <th className="text-left px-4 py-3">Vendor / Bill</th>
                {statusTab === 'PENDING' ? (
                  <th className="text-right px-4 py-3">Action</th>
                ) : (
                  <th className="text-left px-4 py-3">Decision</th>
                )}
              </tr>
            </thead>
            <tbody>
              {items.map((row) => (
                <tr
                  key={row.queue_id}
                  className="border-t border-[#27272a] hover:bg-[#18181b]"
                  data-testid={`reconciliation-row-${row.queue_id}`}
                >
                  <td className="px-4 py-3">
                    <div className="font-medium text-white">{row.ingredient_name || row.ingredient_id}</div>
                    <div className="text-xs text-zinc-500">{row.base_unit}</div>
                  </td>
                  <td className="px-4 py-3 text-zinc-300">{row.business_date || '—'}</td>
                  <td className="px-4 py-3 text-amber-400">{row.anchor_date || '—'}</td>
                  <td className="px-4 py-3 text-right">
                    {formatNumber(row.quantity)} {row.base_unit}
                  </td>
                  <td className="px-4 py-3 text-right text-zinc-300">
                    ₹{formatNumber(row.total_value)}
                  </td>
                  <td className="px-4 py-3 text-zinc-400 text-xs">
                    <div>{row.vendor_name || '—'}</div>
                    <div className="text-zinc-600">{(row.bill_id || '').slice(0, 24)}</div>
                  </td>
                  {statusTab === 'PENDING' ? (
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-2 flex-wrap">
                        <button
                          disabled={!canResolve || resolving}
                          onClick={() => handleQuickDecision(row, 'INCLUDED_IN_AUDIT')}
                          className="px-3 py-1.5 rounded-md bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20 text-xs font-medium disabled:opacity-40 disabled:cursor-not-allowed"
                          data-testid={`resolve-included-${row.queue_id}`}
                          title="Goods were already counted at audit. SOH unchanged."
                        >
                          Included in audit
                        </button>
                        <button
                          disabled={!canResolve || resolving}
                          onClick={() => handleQuickDecision(row, 'NOT_INCLUDED')}
                          className="px-3 py-1.5 rounded-md bg-amber-500/10 text-amber-400 hover:bg-amber-500/20 text-xs font-medium disabled:opacity-40 disabled:cursor-not-allowed"
                          data-testid={`resolve-not-included-${row.queue_id}`}
                          title="Goods were NOT counted. Add full quantity to SOH."
                        >
                          Not included
                        </button>
                        <button
                          disabled={!canResolve || resolving}
                          onClick={() =>
                            setSplitDialog({ row, uncounted: '', notes: '' })
                          }
                          className="px-3 py-1.5 rounded-md bg-blue-500/10 text-blue-400 hover:bg-blue-500/20 text-xs font-medium flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed"
                          data-testid={`resolve-split-${row.queue_id}`}
                          title="Some was counted, some wasn't. Specify the uncounted slice."
                        >
                          <Split className="w-3 h-3" />
                          Split
                        </button>
                      </div>
                    </td>
                  ) : (
                    <td className="px-4 py-3 text-zinc-300">
                      <div className="font-medium">
                        {DECISION_LABEL[row.decision] || row.decision || '—'}
                      </div>
                      <div className="text-xs text-zinc-500">
                        Applied: {formatNumber(row.applied_quantity || 0)} {row.base_unit}
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!canResolve && statusTab === 'PENDING' && items.length > 0 && (
        <p className="text-xs text-zinc-500 mt-4" data-testid="reconciliation-rbac-note">
          You don't have permission to resolve. Only org admins, super admins, or
          procurement managers can resolve reconciliation items.
        </p>
      )}

      {/* Split Dialog */}
      <Dialog open={!!splitDialog} onOpenChange={(o) => !o && setSplitDialog(null)}>
        <DialogContent className="max-w-md" data-testid="reconciliation-split-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Split className="w-5 h-5 text-blue-400" />
              Split decision
            </DialogTitle>
            <DialogDescription>
              Record how much of this bill's quantity was NOT already captured at the
              last audit. Only that slice is added to stock-on-hand.
            </DialogDescription>
          </DialogHeader>
          {splitDialog && (
            <div className="space-y-4">
              <div className="text-sm text-zinc-400">
                Bill quantity:{' '}
                <span className="text-white font-medium">
                  {formatNumber(splitDialog.row.quantity)} {splitDialog.row.base_unit}
                </span>
              </div>
              <div>
                <label className="block text-xs uppercase text-zinc-400 mb-1">
                  Uncounted quantity (will be added to stock)
                </label>
                <input
                  type="number"
                  step="any"
                  min="0"
                  max={splitDialog.row.quantity}
                  value={splitDialog.uncounted}
                  onChange={(e) =>
                    setSplitDialog({ ...splitDialog, uncounted: e.target.value })
                  }
                  className="w-full px-3 py-2 rounded-md bg-[#18181b] border border-[#27272a] text-white"
                  data-testid="reconciliation-split-uncounted-input"
                />
                <p className="text-xs text-zinc-500 mt-1">
                  The remainder ({formatNumber(
                    Math.max(
                      0,
                      splitDialog.row.quantity - parseFloat(splitDialog.uncounted || 0)
                    )
                  )}{' '}
                  {splitDialog.row.base_unit}) will be treated as already counted.
                </p>
              </div>
              <div>
                <label className="block text-xs uppercase text-zinc-400 mb-1">
                  Notes (optional)
                </label>
                <textarea
                  rows={2}
                  value={splitDialog.notes}
                  onChange={(e) =>
                    setSplitDialog({ ...splitDialog, notes: e.target.value })
                  }
                  className="w-full px-3 py-2 rounded-md bg-[#18181b] border border-[#27272a] text-white text-sm"
                  data-testid="reconciliation-split-notes-input"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <button
              onClick={() => setSplitDialog(null)}
              className="px-4 py-2 rounded-md bg-[#27272a] hover:bg-[#3f3f46] text-sm flex items-center gap-2"
              data-testid="reconciliation-split-cancel"
            >
              <X className="w-4 h-4" />
              Cancel
            </button>
            <button
              disabled={resolving}
              onClick={handleSplitSubmit}
              className="px-4 py-2 rounded-md bg-blue-600 hover:bg-blue-500 text-sm font-medium disabled:opacity-40"
              data-testid="reconciliation-split-confirm"
            >
              Apply split
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PurchaseReconciliation;
